import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";

import { GroupedInventarioTable } from "../../../components/BI/GroupedInventarioTable";
import { PaginationControls } from "../../../components/BI/GroupedInventoryTable/PaginationControls";
import FilterBar from "../../../components/BI/FilterBar";
import EditableCell from "../../../components/BI/EditableCell";
import ExportButtons from "../../../components/BI/ExportButtons";
import ImportExcelModal from "../../../components/BI/ImportExcelModal";
import PollingIndicator from "../../../components/BI/PollingIndicator";
import usePolling from "../../../hooks/usePolling";
import {
  fetchInventario,
  setInventarioFilters,
  setInventarioPagination,
  clearInventarioFilters,
} from "../../../slices/biSlice";

const API_URL = process.env.REACT_APP_API_URL;
const POLLING_INTERVAL = 900000; // 15 minutos

// Opciones estáticas para filtros de estado de inventario
const ESTADO_INVENTARIO_OPTIONS = [
  { value: "bajoMinimo", label: "Bajo Objetivo" },
  { value: "enObjetivo", label: "En Objetivo" },
];

export function InventarioReportePage() {
  const dispatch = useDispatch();
  const { data, total, loading, error, lastUpdated, filters, pagination } =
    useSelector((state) => state.bi.inventario);
  const token = useSelector((state) => state.app.user?.token);

  // Vista consolidada toggle
  const [vistaConsolidada, setVistaConsolidada] = useState(
    filters.consolidado || false
  );

  // Import Excel modal state
  const [importModalOpen, setImportModalOpen] = useState(false);

  // Dynamic filter options
  const [marcaOptions, setMarcaOptions] = useState([]);
  const [proveedorOptions, setProveedorOptions] = useState([]);
  const [categoriaOptions, setCategoriaOptions] = useState([]);

  // Fetch filter options on mount
  useEffect(() => {
    if (!token) return;
    const headers = { "Content-type": "application/json", Authorization: `Bearer ${token}` };

    fetch(`${API_URL}/marcas`, { headers })
      .then((res) => res.ok ? res.json() : [])
      .then((data) =>
        setMarcaOptions(
          data
            .map((m) => ({ value: m.nombre, label: m.nombre }))
            .sort((a, b) => a.label.localeCompare(b.label))
        )
      )
      .catch(() => {});

    fetch(`${API_URL}/proveedores`, { headers })
      .then((res) => res.ok ? res.json() : [])
      .then((data) =>
        setProveedorOptions(
          data
            .map((p) => ({ value: String(p.id), label: p.nombre }))
            .sort((a, b) => a.label.localeCompare(b.label))
        )
      )
      .catch(() => {});

    fetch(`${API_URL}/categorias`, { headers })
      .then((res) => res.ok ? res.json() : [])
      .then((data) =>
        setCategoriaOptions(
          data
            .map((c) => ({ value: String(c.id), label: c.nombre || `Categoría ${c.id}` }))
            .sort((a, b) => a.label.localeCompare(b.label))
        )
      )
      .catch(() => {});
  }, [token]);

  // Polling: fetch inventario cada 15 min
  const fetchData = useCallback(() => {
    return dispatch(fetchInventario());
  }, [dispatch]);

  const { lastUpdated: pollingLastUpdated, isStale, refetch } = usePolling(
    fetchData,
    POLLING_INTERVAL
  );

  // Timestamp de última actualización (preferir el de Redux si existe)
  const displayLastUpdated = lastUpdated || pollingLastUpdated;

  // ============================================================
  // Configuración de filtros para FilterBar
  // ============================================================
  const filterConfig = useMemo(
    () => [
      {
        name: "busqueda",
        label: "Buscar",
        type: "text",
        placeholder: "Nombre o marca...",
      },
      {
        name: "marca",
        label: "Marca",
        type: "select",
        options: marcaOptions,
        placeholder: "Todas las marcas",
      },
      {
        name: "proveedor",
        label: "Proveedor",
        type: "select",
        options: proveedorOptions,
        placeholder: "Todos los proveedores",
      },
      {
        name: "bodega",
        label: "Bodega",
        type: "select",
        options: [
          { value: "1", label: "Heredia" },
          { value: "2", label: "Guácimo" },
          { value: "3", label: "Santa Ana" },
        ],
      },
      {
        name: "categoria",
        label: "Categoría",
        type: "select",
        options: categoriaOptions,
        placeholder: "Todas las categorías",
      },
      {
        name: "peso",
        label: "Peso (Kg)",
        type: "range",
        min: 0,
        max: 50,
      },
      {
        name: "estadoInventario",
        label: "Estado Inventario",
        type: "select",
        options: ESTADO_INVENTARIO_OPTIONS,
      },
    ],
    [marcaOptions, proveedorOptions, categoriaOptions]
  );

  // ============================================================
  // Handlers
  // ============================================================
  const debounceRef = useRef(null);

  const handleFilterChange = useCallback(
    (name, value) => {
      dispatch(setInventarioFilters({ [name]: value || null }));

      // Debounce text filters to avoid excessive API calls
      if (name === "busqueda") {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
          dispatch(fetchInventario());
        }, 400);
      } else {
        dispatch(fetchInventario());
      }
    },
    [dispatch]
  );

  const handleClearFilters = useCallback(() => {
    dispatch(clearInventarioFilters());
    setVistaConsolidada(false);
    dispatch(fetchInventario());
  }, [dispatch]);

  const handlePageChange = useCallback(
    (skip) => {
      dispatch(setInventarioPagination({ skip }));
      dispatch(fetchInventario());
    },
    [dispatch]
  );

  const handlePageSizeChange = useCallback(
    (limit) => {
      dispatch(setInventarioPagination({ limit, skip: 0 }));
      dispatch(fetchInventario());
    },
    [dispatch]
  );

  const handleConsolidadoToggle = useCallback(() => {
    const newValue = !vistaConsolidada;
    setVistaConsolidada(newValue);
    dispatch(setInventarioFilters({ consolidado: newValue }));
    dispatch(fetchInventario());
  }, [vistaConsolidada, dispatch]);

  // Handler para guardar inventarioMinimo / inventarioObjetivo
  const handleSaveConfig = useCallback(
    async (row, field, newValue) => {
      const url = `${API_URL}/bi/configuracion-inventario/${row.idProducto}/${row.idBodega}`;
      const response = await fetch(url, {
        method: "PATCH",
        headers: {
          "Content-type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ [field]: newValue }),
      });
      if (!response.ok) {
        const errText = await response.text().catch(() => "Error al guardar");
        throw new Error(errText || `HTTP ${response.status}`);
      }
      // Refrescar datos tras guardar
      dispatch(fetchInventario());
    },
    [token, dispatch]
  );

  // Handler para restaurar inventarioMinimo al valor calculado
  const handleRestaurarMinimo = useCallback(
    async (row) => {
      const url = `${API_URL}/bi/configuracion-inventario/${row.idProducto}/${row.idBodega}/restaurar-minimo`;
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        const errText = await response.text().catch(() => "Error al restaurar");
        throw new Error(errText || `HTTP ${response.status}`);
      }
      dispatch(fetchInventario());
    },
    [token, dispatch]
  );

  // ============================================================
  // Consolidate data when toggle is ON
  // ============================================================
  const consolidatedData = useMemo(() => {
    if (!vistaConsolidada || !data || data.length === 0) return null;

    const productMap = {};
    for (const row of data) {
      const key = row.idProducto;
      if (!productMap[key]) {
        productMap[key] = {
          idProducto: row.idProducto,
          codigoProveedor: row.codigoProveedor,
          marca: row.marca,
          nombreProducto: row.nombreProducto,
          pesoKg: row.pesoKg,
          bodega: "Todas",
          existenciaActual: 0,
          inventarioComprometido: 0,
          inventarioDisponible: 0,
          inventarioEnTransito: 0,
          inventarioProyectado: 0,
          consumo30Dias: 0,
          inventarioMinimo: 0,
          coberturaDias: null,
          sugerenciaPedido: 0,
          sugerenciaTransferencia: 0,
        };
      }
      const p = productMap[key];
      p.existenciaActual += row.existenciaActual || 0;
      p.inventarioComprometido += row.inventarioComprometido || 0;
      p.inventarioDisponible += row.inventarioDisponible || 0;
      p.inventarioEnTransito += row.inventarioEnTransito || 0;
      p.inventarioProyectado += row.inventarioProyectado || 0;
      p.consumo30Dias += row.consumo30Dias || 0;
      p.inventarioMinimo += row.inventarioMinimo || 0;
      p.sugerenciaPedido += row.sugerenciaPedido || 0;
      p.sugerenciaTransferencia += row.sugerenciaTransferencia || 0;
    }

    // Calculate coberturaDias and sugerencias for consolidated
    for (const p of Object.values(productMap)) {
      const pvd = p.consumo30Dias / 30;
      if (pvd <= 0) {
        p.coberturaDias = 99999;
      } else {
        p.coberturaDias = p.inventarioDisponible / pvd;
      }
      // Recalculate sugerenciaPedido: max(0, objetivo - proyectado)
      p.sugerenciaPedido = Math.max(0, p.inventarioMinimo - p.inventarioProyectado);
      // Recalculate sugerenciaTransferencia: not applicable in consolidated view
      p.sugerenciaTransferencia = 0;
    }

    return Object.values(productMap);
  }, [vistaConsolidada, data]);

  // Columns for consolidated view (no bodega, no editable cells, no transferencia)
  const consolidatedColumns = useMemo(
    () => [
      { key: "idProducto", label: "ID Producto" },
      { key: "codigoProveedor", label: "Cód. Proveedor" },
      { key: "marca", label: "Marca" },
      { key: "nombreProducto", label: "Producto" },
      {
        key: "pesoKg",
        label: "Peso (Kg)",
        render: (row) => row.pesoKg?.toFixed(2) ?? "–",
      },
      { key: "existenciaActual", label: "Existencia" },
      { key: "inventarioComprometido", label: "Comprometido" },
      { key: "inventarioDisponible", label: "Disponible" },
      { key: "inventarioEnTransito", label: "En Tránsito" },
      { key: "inventarioProyectado", label: "Proyectado" },
      { key: "consumo30Dias", label: "Ventas 30 días" },
      { key: "inventarioMinimo", label: "Inv. Objetivo" },
      {
        key: "coberturaDias",
        label: "Cobertura (días)",
        render: (row) => {
          if (row.coberturaDias == null) return "–";
          if (row.coberturaDias >= 99999) return "∞ (sin ventas)";
          if (row.coberturaDias < 0) return "⚠️ Déficit";
          return row.coberturaDias.toFixed(1);
        },
      },
      { key: "sugerenciaPedido", label: "Sug. Pedido" },
    ],
    []
  );
  // ============================================================
  // Columnas de la tabla (vista por bodega)
  // ============================================================
  const columns = useMemo(
    () => [
      { key: "idProducto", label: "ID Producto", sortable: true },
      { key: "codigoProveedor", label: "Cód. Proveedor", sortable: true },
      { key: "marca", label: "Marca", sortable: true },
      { key: "nombreProducto", label: "Producto", sortable: true },
      {
        key: "pesoKg",
        label: "Peso (Kg)",
        sortable: true,
        render: (row) => row.pesoKg?.toFixed(2) ?? "–",
      },
      { key: "bodega", label: "Bodega", sortable: true },
      { key: "existenciaActual", label: "Existencia", sortable: true },
      {
        key: "inventarioComprometido",
        label: "Comprometido",
        sortable: true,
      },
      { key: "inventarioDisponible", label: "Disponible", sortable: true },
      { key: "inventarioEnTransito", label: "En Tránsito", sortable: true },
      { key: "inventarioProyectado", label: "Proyectado", sortable: true },
      { key: "consumo30Dias", label: "Ventas 30 días", sortable: true },
      {
        key: "inventarioMinimo",
        label: "Inv. Objetivo",
        sortable: true,
        render: (row) => (
          <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <EditableCell
              value={row.inventarioMinimo}
              min={0}
              max={99999}
              onSave={(newValue) =>
                handleSaveConfig(row, "inventarioMinimo", newValue)
              }
            />
            {row.minimoOverride && (
              <button
                onClick={() => handleRestaurarMinimo(row)}
                title={`Restaurar al valor calculado (${row.inventarioMinimoCalculado})`}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: "2px",
                  fontSize: "14px",
                  lineHeight: 1,
                  color: "#6b7280",
                }}
              >
                ↺
              </button>
            )}
          </div>
        ),
      },
      {
        key: "coberturaDias",
        label: "Cobertura (días)",
        sortable: true,
        render: (row) => {
          if (row.coberturaDias == null) return "–";
          if (row.coberturaDias >= 99999) return "∞ (sin ventas)";
          if (row.coberturaDias < 0) return "⚠️ Déficit";
          return row.coberturaDias.toFixed(1);
        },
      },
      { key: "sugerenciaPedido", label: "Sug. Pedido", sortable: true },
      {
        key: "sugerenciaTransferencia",
        label: "Sug. Transferencia",
        sortable: true,
      },
    ],
    [handleSaveConfig]
  );

  // ============================================================
  // Export URL
  // ============================================================
  const exportUrl = useMemo(() => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, val]) => {
      if (val != null && val !== "") {
        params.append(key, val);
      }
    });
    if (token) params.append("access_token", token);
    const qs = params.toString();
    return `${API_URL}/bi/inventario/export/excel${qs ? `?${qs}` : ""}`;
  }, [filters, token]);

  // Export consolidado (client-side xlsx)
  const handleExportConsolidado = useCallback(() => {
    if (!consolidatedData || consolidatedData.length === 0) return;

    import("xlsx").then((XLSX) => {
      const wsData = [
        [
          "ID Producto", "Cód. Proveedor", "Marca", "Producto", "Peso (Kg)",
          "Existencia", "Comprometido", "Disponible", "En Tránsito", "Proyectado",
          "Ventas 30 días", "Inv. Objetivo", "Cobertura (días)", "Sug. Pedido",
        ],
      ];

      for (const row of consolidatedData) {
        const cobertura = row.coberturaDias >= 99999
          ? "∞ (sin ventas)"
          : row.coberturaDias < 0
            ? "Déficit"
            : Number(row.coberturaDias.toFixed(1));

        wsData.push([
          row.idProducto,
          row.codigoProveedor || "",
          row.marca || "",
          row.nombreProducto || "",
          row.pesoKg != null ? Number(row.pesoKg.toFixed(2)) : "",
          row.existenciaActual,
          row.inventarioComprometido,
          row.inventarioDisponible,
          row.inventarioEnTransito,
          row.inventarioProyectado,
          row.consumo30Dias,
          row.inventarioMinimo,
          cobertura,
          row.sugerenciaPedido,
        ]);
      }

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet(wsData);

      // Set column widths
      ws["!cols"] = [
        { wch: 10 }, { wch: 16 }, { wch: 18 }, { wch: 40 }, { wch: 10 },
        { wch: 12 }, { wch: 14 }, { wch: 12 }, { wch: 12 }, { wch: 12 },
        { wch: 14 }, { wch: 12 }, { wch: 14 }, { wch: 12 },
      ];

      XLSX.utils.book_append_sheet(wb, ws, "Inventario Consolidado");
      XLSX.writeFile(wb, `inventario-consolidado-${new Date().toISOString().slice(0, 10)}.xlsx`);
    });
  }, [consolidatedData]);

  // ============================================================
  // Render
  // ============================================================
  return (
    <div>
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
        <h1 className="text-xl font-bold text-gray-800">
          Reporte de Inventario
        </h1>
        <div className="flex items-center gap-3 ml-auto">
          <PollingIndicator
            lastUpdated={displayLastUpdated}
            isStale={isStale}
            onRefresh={refetch}
          />
          <ExportButtons
            exportUrl={vistaConsolidada ? undefined : exportUrl}
            onExportExcel={vistaConsolidada ? handleExportConsolidado : undefined}
          />
          <button
            type="button"
            onClick={() => setImportModalOpen(true)}
            className="inline-flex items-center gap-2 bg-purple text-white text-sm font-medium py-2 px-4 rounded hover:bg-purple/90 transition-colors"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
              />
            </svg>
            Importar Excel
          </button>
        </div>
      </div>

      {/* Error / stale data message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 flex items-center gap-2">
          <svg
            className="w-5 h-5 flex-shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span className="text-sm">
            Error al cargar datos: {error}.
            {displayLastUpdated && (
              <> Mostrando última consulta exitosa.</>
            )}
          </span>
          <button
            onClick={refetch}
            className="ml-auto text-sm text-red-700 underline hover:text-red-900"
          >
            Reintentar
          </button>
        </div>
      )}

      {/* Filtros */}
      <FilterBar
        filters={filterConfig}
        values={filters}
        onChange={handleFilterChange}
        onClear={handleClearFilters}
      />

      {/* Toggle vista consolidada */}
      <div className="flex items-center gap-3 mb-4">
        <label className="inline-flex items-center gap-2 text-sm text-baseGray cursor-pointer">
          <input
            type="checkbox"
            checked={vistaConsolidada}
            onChange={handleConsolidadoToggle}
            className="rounded border-borderGray text-purple focus:ring-purple"
          />
          Totalizar (sin separar por bodega)
        </label>
        <span className="text-xs text-borderGray">
          {vistaConsolidada
            ? "Mostrando totales consolidados por producto"
            : "Mostrando detalle por bodega"}
        </span>
      </div>

      {/* Tabla de datos */}
      {vistaConsolidada ? (
        <GroupedInventarioTable
          data={consolidatedData}
          columns={consolidatedColumns}
          loading={loading}
          emptyMessage="No hay datos de inventario disponibles."
          identityKeys={["idProducto", "codigoProveedor", "marca", "nombreProducto", "pesoKg"]}
          groupByKey="idProducto"
        />
      ) : (
        <GroupedInventarioTable
          data={data}
          columns={columns}
          loading={loading}
          emptyMessage="No hay datos de inventario disponibles."
          identityKeys={["idProducto", "codigoProveedor", "marca", "nombreProducto", "pesoKg"]}
          groupByKey="idProducto"
        />
      )}

      {/* Paginación */}
      {!vistaConsolidada && pagination && total > 0 && (
        <PaginationControls
          limit={pagination.limit}
          skip={pagination.skip}
          total={total}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          pageOptions={[50, 100, 200]}
        />
      )}
      {vistaConsolidada && consolidatedData && consolidatedData.length > 0 && (
        <div className="flex items-center mt-4 px-2 text-sm text-baseGray">
          <span>Mostrando {consolidatedData.length} productos (totalizado de {total} filas)</span>
        </div>
      )}

      {/* Modal de importación Excel */}
      <ImportExcelModal
        open={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        token={token}
        onSuccess={() => dispatch(fetchInventario())}
      />
    </div>
  );
}

export default InventarioReportePage;
